if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.isProgrammaticScroll = false;
      }

      connectedCallback() {
        if (this.dataset.carouselReady === 'true') return;

        this.slider = this.querySelector('[id^="Slider-Gallery"]');
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          swipeTarget: this.querySelector('.product__media-viewer'),
        };

        const items = this.getMediaItems();
        if (!this.slider || items.length < 2) return;

        this.dataset.carouselReady = 'true';
        this.currentIndex = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));

        this.addEventListener('click', this.onCarouselClick.bind(this));

        if (this.slider) {
          this.slider.addEventListener('scroll', debounce(this.onSliderScroll.bind(this), 100));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch.querySelector('button')?.addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
        }

        this.initSwipe();
        this.goToSlide(this.currentIndex, false);
      }

      onCarouselClick(event) {
        const button = event.target.closest('[data-direction]');
        if (!button || !this.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();
        this.navigateMedia(button.dataset.direction);
      }

      initSwipe() {
        const swipeTarget = this.elements.swipeTarget;
        if (!swipeTarget) return;

        this.swipeState = { startX: 0, startY: 0, isDragging: false };

        swipeTarget.addEventListener(
          'touchstart',
          (event) => {
            this.swipeState.startX = event.touches[0].clientX;
            this.swipeState.startY = event.touches[0].clientY;
            this.swipeState.isDragging = true;
          },
          { passive: true }
        );

        swipeTarget.addEventListener(
          'touchend',
          (event) => {
            if (!this.swipeState.isDragging) return;
            this.swipeState.isDragging = false;
            this.handleSwipe(
              this.swipeState.startX,
              this.swipeState.startY,
              event.changedTouches[0].clientX,
              event.changedTouches[0].clientY
            );
          },
          { passive: true }
        );
      }

      handleSwipe(startX, startY, endX, endY) {
        const diffX = startX - endX;
        const diffY = startY - endY;

        if (Math.abs(diffX) < 40 || Math.abs(diffX) < Math.abs(diffY)) return;

        this.navigateMedia(diffX > 0 ? 'next' : 'prev');
      }

      getMediaItems() {
        if (!this.slider) return [];
        return Array.from(this.slider.querySelectorAll(':scope > li.product__media-item'));
      }

      navigateMedia(direction) {
        const items = this.getMediaItems();
        if (items.length < 2) return;

        const step = direction === 'next' ? 1 : -1;
        this.goToSlide(this.currentIndex + step);
      }

      goToSlide(index, animate = true) {
        const items = this.getMediaItems();
        if (!items.length) return;

        this.currentIndex = ((index % items.length) + items.length) % items.length;
        const slide = items[this.currentIndex];

        items.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === this.currentIndex);
        });

        this.isProgrammaticScroll = true;
        slide.scrollIntoView({
          behavior: animate ? 'smooth' : 'auto',
          inline: 'center',
          block: 'nearest',
        });

        window.setTimeout(() => {
          this.isProgrammaticScroll = false;
        }, animate ? 450 : 0);

        this.playActiveMedia(slide);
        this.syncThumbnail(slide);
      }

      onSliderScroll() {
        if (this.isProgrammaticScroll) return;

        const items = this.getMediaItems();
        if (!items.length || !this.slider) return;

        const sliderCenter = this.slider.scrollLeft + this.slider.clientWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        items.forEach((item, itemIndex) => {
          const itemCenter = item.offsetLeft + item.offsetWidth / 2;
          const distance = Math.abs(itemCenter - sliderCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = itemIndex;
          }
        });

        if (closestIndex === this.currentIndex) return;

        this.currentIndex = closestIndex;
        items.forEach((item, itemIndex) => {
          item.classList.toggle('is-active', itemIndex === this.currentIndex);
        });
        this.syncThumbnail(items[this.currentIndex]);
      }

      syncThumbnail(slide) {
        if (!this.elements.thumbnails || !slide) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${slide.dataset.mediaId}"]`);
        if (!activeThumbnail) return;

        this.elements.thumbnails.querySelectorAll('button').forEach((button) => {
          button.removeAttribute('aria-current');
        });
        activeThumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible?.(activeThumbnail, 10)) {
          this.announceLiveRegion(slide, activeThumbnail.dataset.mediaPosition);
          return;
        }

        this.elements.thumbnails.slider?.scrollTo({ left: activeThumbnail.offsetLeft, behavior: 'smooth' });
        this.announceLiveRegion(slide, activeThumbnail.dataset.mediaPosition);
      }

      setActiveMedia(mediaId, prepend) {
        let items = this.getMediaItems();
        let activeMedia = this.slider?.querySelector(`:scope > li.product__media-item[data-media-id="${mediaId}"]`);

        if (!activeMedia) return;

        if (prepend) {
          if (activeMedia.parentElement.firstChild !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          items = this.getMediaItems();
          activeMedia = this.slider?.querySelector(`:scope > li.product__media-item[data-media-id="${mediaId}"]`);
        }

        const nextIndex = items.indexOf(activeMedia);
        if (nextIndex === -1) return;

        this.preventStickyHeader();
        this.goToSlide(nextIndex);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;

        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        window.setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }
    }
  );
}
